import { getPopularProducts } from '@koala/sdk/v4';
import { QueryClient, Hydrate } from '@tanstack/react-query';
import { type InferGetServerSidePropsType, type GetServerSideProps } from 'next';
import { getConfigOverride } from '@/features/configs/configs';
import { getOrganization } from '@/features/configs/organization';
import LocationsFinder from '@/features/locations/finder';
import { createHttpClient } from '@/services/client';
import { getFooterTemplate } from '@/services/footer.service';
import { type ServerQuery, queryOnServer } from '@/services/ssr.service';
import { getOrigin } from '@/utils';

function Stores({ serverState }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <Hydrate state={serverState}>
      {/* @ts-expect-error */}
      <LocationsFinder />
    </Hydrate>
  );
}

interface PageProps {
  serverState?: unknown;
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({ req }) => {
  if (!req.headers.host) {
    throw new Error('Missing request origin');
  }

  const queryClient = new QueryClient();
  const origin = getOrigin(req.headers.host);
  const organizationConfig = await getOrganization(queryClient, origin);
  const isSingleLocation = organizationConfig?.locations.features.isSingleLocation;
  /**
   * Redirect to store page if it is single store location organization/brand
   */
  if (isSingleLocation?.storeLocationId) {
    return {
      redirect: {
        destination: `/store/${isSingleLocation.storeLocationId}`,
        permanent: false,
      },
    };
  }

  const { flagship_store_location } = await getConfigOverride('menu', queryClient, origin);

  let serverQueries: ServerQuery[] = [
    {
      queryKey: ['site-footer'],
      queryFn: () => getFooterTemplate({ origin }),
    },
  ];

  if (flagship_store_location) {
    serverQueries = serverQueries.concat([
      {
        queryKey: ['popular-items', { id: flagship_store_location }],
        queryFn: () =>
          getPopularProducts(
            { locationId: flagship_store_location, maxItems: 15 },
            { client: createHttpClient({ origin }) },
          ),
      },
    ]);
  }

  const serverState = await queryOnServer(serverQueries, queryClient);

  return {
    props: {
      serverState,
    },
  };
};

export default Stores;
